import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaUniversity, FaCopy, FaCheck, FaHistory } from 'react-icons/fa';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';

const WithdrawMoney = () => {
  const [walletBalance, setWalletBalance] = useState(0);
  const [formData, setFormData] = useState({
    amount: '',
    accountHolder: '',
    bankAccount: '',
    ifsc: ''
  });
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchWalletBalance();
    fetchWithdrawals();
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWalletBalance(response.data.wallet?.balance || 0);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/withdrawals/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawals(response.data.withdrawals);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(formData.amount);
    const MIN_WITHDRAWAL = 500;
    
    if (amount < MIN_WITHDRAWAL) {
      toast.error(`Minimum withdrawal amount is ₹${MIN_WITHDRAWAL}`);
      return;
    }
    
    if (amount > walletBalance) {
      toast.error('Insufficient wallet balance');
      return;
    }
    
    if (!formData.accountHolder || !formData.bankAccount || !formData.ifsc) {
      toast.error('Please fill all bank details');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/withdrawals/request`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Withdrawal request submitted successfully!');
      setFormData({ amount: '', accountHolder: '', bankAccount: '', ifsc: '' });
      fetchWalletBalance();
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Withdrawal failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    return <span className={`px-2 py-1 text-xs rounded-full ${styles[status]}`}>{status}</span>;
  };

  return (
    <>
      <Helmet>
        <title>Withdraw Money - Assets Dairy Farm</title>
      </Helmet>

      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Withdraw Money</h1>
          <p className="text-gray-500 mb-8">Withdraw funds from your wallet to your bank account</p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Withdraw Form */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="bg-green-50 rounded-xl p-4 mb-6 text-center">
                <p className="text-gray-600 text-sm">Available Balance</p>
                <p className="text-3xl font-bold text-green-600">₹{walletBalance.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Minimum withdrawal: ₹500</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <div className="relative">
                    <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                      min="500"
                      max={walletBalance}
                      className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Enter amount"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimum: ₹500</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
                  <input
                    type="text"
                    name="accountHolder"
                    value={formData.accountHolder}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="As per bank account"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account Number</label>
                  <input
                    type="text"
                    name="bankAccount"
                    value={formData.bankAccount}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Enter bank account number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Enter IFSC code"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Request Withdrawal'}
                </button>
              </form>

              <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-xs text-yellow-800">
                <strong>Note:</strong> Withdrawal requests are processed within 2-3 business days after admin approval.
              </div>
            </div>

            {/* Withdrawal History */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FaHistory /> Withdrawal History
              </h2>
              {withdrawals.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No withdrawal requests yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {withdrawals.map((w) => (
                    <div key={w._id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-green-600">₹{w.amount.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">{new Date(w.createdAt).toLocaleDateString()}</p>
                        </div>
                        {getStatusBadge(w.status)}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        <p>Bank: {w.bankAccount}</p>
                        {w.notes && <p className="text-red-500 mt-1">{w.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WithdrawMoney;